'use client';

/**
 * RankedListCard — shared visual primitive: rows of {name, secondary
 * line, right-aligned $ value}, already sorted by value. No card in
 * this codebase covered this exact shape (closest precedents:
 * ProductLeaf's row markup in explorer/cards/vendors/list-card.tsx,
 * and the sort-by-value convention in
 * canvas/cards/system_obeya/high-cc-functions-table-card.tsx) — this
 * extracts it once so it isn't hand-rolled a second time. Reused here
 * by both the vendor and product rankings in Plan Commercial Position.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';

export interface RankedListRow {
  key: string;
  name: string;
  secondary?: string;
  value: number;
  valueSuffix?: string;
}

interface RankedListCardProps {
  title: string;
  rows: RankedListRow[];
  emptyLabel?: string;
}

export function RankedListCard({ title, rows, emptyLabel = 'Nothing to show yet' }: RankedListCardProps) {
  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-sm h-full flex flex-col">
      <div className={cn(text.heading)}>{title}</div>
      <div className="mt-3 flex flex-col gap-2 overflow-auto">
        {rows.length === 0 ? (
          <div className={cn(text.muted)}>{emptyLabel}</div>
        ) : (
          rows.map((r) => (
            <div key={r.key} className="flex items-baseline justify-between border-b border-border pb-2">
              <div className="min-w-0">
                <div className={cn(text.body, 'truncate')}>{r.name}</div>
                {r.secondary && <div className={cn(text.muted)}>{r.secondary}</div>}
              </div>
              <div className={cn(text.body, 'tabular-nums shrink-0 pl-3')}>
                ${r.value.toLocaleString()}{r.valueSuffix ?? ''}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
